import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Trophy, Check } from 'lucide-react';
import StatisticalSummary from './StatisticalSummary';
import './ExperimentResults.css';

const VARIANT_COLORS = ['#8B6F4E', '#6B8E6B', '#B8860B', '#708090', '#CD853F'];

function ExperimentResults({ experimentId, experiment }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [computing, setComputing] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const intervalRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      const params = {};
      if (selectedGoal) params.goal_id = selectedGoal;
      const response = await axios.get(`/api/experiments/${experimentId}/results`, { params });
      setResults(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch results:', err);
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [experimentId, selectedGoal]);

  useEffect(() => {
    setLoading(true);
    fetchResults();
  }, [fetchResults]);

  // Auto-refresh every 30s when running
  useEffect(() => {
    if (experiment?.status === 'running') {
      intervalRef.current = setInterval(fetchResults, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [experiment?.status, fetchResults]);

  const handleCompute = async () => {
    setComputing(true);
    try {
      await axios.post(`/api/experiments/${experimentId}/compute`);
      await fetchResults();
    } catch (err) {
      console.error('Failed to compute results:', err);
    } finally {
      setComputing(false);
    }
  };

  if (loading) {
    return (
      <div className="experiment-results">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="experiment-results">
        <div className="exp-error">{error}</div>
      </div>
    );
  }

  const variantResults = results?.variants || [];
  const controlResult = variantResults.find((v) => v.is_control);
  const maxRate = Math.max(...variantResults.map((v) => v.conversion_rate || 0), 0.001);
  const winnerId = results?.winner_variant_id;
  const goals = experiment?.goals || [];

  return (
    <div className="experiment-results">
      <div className="exp-results-header">
        <div className="exp-results-header-left">
          {goals.length > 1 && (
            <select
              className="exp-filter-select"
              value={selectedGoal || ''}
              onChange={(e) => setSelectedGoal(e.target.value || null)}
            >
              <option value="">All Goals</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.event_name} ({g.goal_type}){g.is_primary ? ' - Primary' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          className="exp-btn-secondary"
          onClick={handleCompute}
          disabled={computing}
        >
          <RefreshCw size={14} className={computing ? 'exp-spin' : ''} />
          {computing ? 'Computing...' : 'Recompute'}
        </button>
      </div>

      <div className="exp-results-cards">
        {variantResults.map((v, i) => {
          const isWinner = v.variant_id === winnerId;
          const lift = controlResult && !v.is_control && controlResult.conversion_rate > 0
            ? ((v.conversion_rate - controlResult.conversion_rate) / controlResult.conversion_rate)
            : null;
          const barWidth = maxRate > 0 ? ((v.conversion_rate || 0) / maxRate) * 100 : 0;
          const isSignificant = v.p_value != null && v.p_value < 0.05;

          return (
            <div key={v.variant_id || i} className={`exp-result-card ${isWinner ? 'winner' : ''}`}>
              <div className="exp-result-card-header">
                <div className="exp-result-variant-label">
                  <div
                    className="exp-result-color"
                    style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
                  />
                  <span className="exp-result-variant-name">
                    {v.variant_name || v.variant_key}
                  </span>
                  {v.is_control && <span className="variant-control-badge">Control</span>}
                  {isWinner && (
                    <span className="exp-winner-badge">
                      <Trophy size={12} /> Winner
                    </span>
                  )}
                </div>
                <div className="exp-result-significance">
                  {isSignificant ? (
                    <span className="exp-sig-yes"><Check size={14} /> Significant</span>
                  ) : (
                    <span className="exp-sig-no">Not significant</span>
                  )}
                </div>
              </div>

              <div className="exp-result-metrics">
                <div className="exp-result-metric">
                  <span className="exp-result-metric-value">{(v.visitors ?? 0).toLocaleString()}</span>
                  <span className="exp-result-metric-label">Visitors</span>
                </div>
                <div className="exp-result-metric">
                  <span className="exp-result-metric-value">{(v.conversions ?? 0).toLocaleString()}</span>
                  <span className="exp-result-metric-label">Conversions</span>
                </div>
                <div className="exp-result-metric">
                  <span className="exp-result-metric-value">
                    {v.conversion_rate != null ? (v.conversion_rate * 100).toFixed(2) + '%' : '--'}
                  </span>
                  <span className="exp-result-metric-label">Conv. Rate</span>
                </div>
                {lift !== null && (
                  <div className="exp-result-metric">
                    <span className={`exp-result-metric-value ${lift >= 0 ? 'stats-positive' : 'stats-negative'}`}>
                      {lift >= 0 ? '+' : ''}{(lift * 100).toFixed(2)}%
                    </span>
                    <span className="exp-result-metric-label">Lift vs Control</span>
                  </div>
                )}
              </div>

              <div className="exp-result-bar-wrapper">
                <div
                  className="exp-result-bar"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length],
                  }}
                />
              </div>

              {v.stats && (
                <div className="exp-result-stats-section">
                  <StatisticalSummary stats={v.stats} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {variantResults.length === 0 && (
        <div className="exp-empty">
          <p>No results yet. Start the experiment and wait for data to accumulate.</p>
        </div>
      )}
    </div>
  );
}

export default ExperimentResults;
